<template>
	<div>
		<component
			:is="route.meta.layout || layout"
			v-if="isLoaded && !!route.name"
			:key="layout"
		>
			<router-view />
			<user-tracker />
		</component>

		<NotificationsDispatcher />
		<PurchaseOptionsModal />
		<BulletGeneratorPaywallModal />
		<AutoApplyLimitReachedModal />
		<ServiceNudgeModal />
		<ExistingUserAutoApplyFlow />
		<AuthPopup />
		<DidYouApplyPrompt />
		<AutoApplyPromoModal v-if="isLoaded" />
		<Toaster />
		<div id="modals-container" />
		<div id="tooltips-container" />
		<div id="menus-container" />
	</div>
</template>

<script setup>
import { ref, watch, onMounted, shallowRef } from 'vue';
import { useRoute } from 'vue-router';
// TODO: REVERT - Remove hashInput from this import when Typeform post-onboarding experiment is reverted
import {
	hashInput,
	hasServerSession,
} from './utils/helpers';

import defaultLayout from './layouts/default.vue';

import ServiceNudgeModal from './modals/services-nudges/service-nudge-modal.vue';
import ExistingUserAutoApplyFlow from './modals/existing-user-auto-apply/existing-user-auto-apply-flow.vue';
import PurchaseOptionsModal from './modals/premium-plans-modal.vue';
import AutoApplyPromoModal from './modals/auto-apply-promo/auto-apply-promo-modal.vue';
import BulletGeneratorPaywallModal from './modals/bullet-generator-paywall-modal.vue';
import AutoApplyLimitReachedModal from './modals/auto-apply-limit-reached-modal.vue';

import UserTracker from './components/user-tracker.vue';
import NotificationsDispatcher from './components/notifications/notifications-dispatcher.vue';
import AuthPopup from './components/auth/auth-popup.vue';
import DidYouApplyPrompt from './components/job-board/did-you-apply-prompt.vue';
import { Toaster } from './components/ui/sonner';

import { setAuthState } from './composables/auth';
import { usePremiumNudgeStore } from './store/nudge-premium.store';
import { update as updateProfile } from './api/profile';

import axios from './utils/axios';

const route = useRoute();

const premiumNudgeStore = usePremiumNudgeStore();
const { isAuthenticated, setUser, setUserData, getUser } = setAuthState();

// The Inertia onboarding flow authenticates via the Laravel session but never writes the
// SPA's localStorage user, so isAuthenticated() (localStorage-based) is false on the handoff
// even though the session is valid. welcome.blade.php exposes the authenticated id, so a
// present id means there is a server session to hydrate from /api/user (see onMounted).
const serverSession = hasServerSession();
const isLoaded = ref(!isAuthenticated() && !serverSession);

const saveUTMParamsToLocalStorage = (queryParams) => {
	const utmParams = ['utm_medium', 'utm_source', 'utm_campaign', 'utm_content', 'utm_term'];

	// Check if any UTM params exist in the query
	utmParams.forEach((param) => {
		if (queryParams[param]) {
			localStorage.setItem(param, queryParams[param]);
		}
	});
};

watch(() => route.query, () => {
	saveUTMParamsToLocalStorage(route.query);
}, { immediate: true });

const layout = shallowRef(defaultLayout);

watch(route, async () => {
	// Its setShowModal() would replace the close handler of a checkout already on screen.
	if (route.meta?.suppressGlobalPrompts) {
		return;
	}

	const nudgeUser = getUser();
	if (nudgeUser?.options?.show_premium_nudge === true) {
		// Don't show premium nudge to already-subscribed users — just clear the flag
		if (nudgeUser?.current_plan) {
			// Optimistically clear the flag to prevent duplicate API calls on rapid navigation
			setUser({ ...nudgeUser, options: { ...nudgeUser.options, show_premium_nudge: false } });
			const user = await updateProfile({
				options: { ...nudgeUser.options, show_premium_nudge: false }
			});
			setUser(user.data.data);
			return;
		}

		premiumNudgeStore.setShowModal(async () => {
			const onboardingBody = {
				...getUser()?.options,
				show_premium_nudge: false
			};

			const user = await updateProfile({ options: onboardingBody });
			setUser(user.data.data);

			// TODO: REVERT - Remove this block when Typeform post-onboarding experiment is reverted
			// After premium nudge is closed, trigger Typeform for users in allowed locations
			// Skip if user just subscribed — no need to show sales call Typeform
			const currentUser = getUser();
			if (currentUser?.options?.show_typeform_after_premium_nudge === true && !currentUser?.current_plan) {
				localStorage.setItem(`newTypeformAppointment${hashInput(currentUser?.email ?? '')}`, true);
				window.dispatchEvent(new Event('show-typeform-appointment'));
				const updatedUser = await updateProfile({
					options: {
						show_typeform_after_premium_nudge: false,
						typeform_shown: true
					}
				});
				setUser(updatedUser.data.data);
			}
		});
	}
});

onMounted(() => {
	// first time check if cookie is not expired, if expired user gets redirected to login page
	if (isAuthenticated() || serverSession) {
		axios.get('/api/user').then((res) => {
			// The subscription, feature-usage, onboarding-checklist and partner stores hang off the
			// same payload, so they refresh with it rather than keep what the last login left.
			setUserData(res?.data?.data);

			isLoaded.value = true;
		}).catch(() => {
			// A failed /api/user must not surface as an unhandled rejection or leave the
			// SPA stuck behind the loading gate; render with whatever auth state we have.
			isLoaded.value = true;
		});
	}
});
</script>
